"use client";

import { ChevronLeft, ChevronRight, type LucideIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { InlineCombobox } from "@/components/ui/inline-combobox";
import { cn } from "@/lib/utils";
import { buildPageList, formatTotal } from "./page-list";
import type { RecordTotal } from "./types";

interface RecordPaginationProps {
  page: number;
  pageSize: number;
  total: RecordTotal;
  rowCount: number;
  canLoadMore?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const PAGE_SIZES = [25, 50, 100];

function describeRowRange(
  page: number,
  pageSize: number,
  rowCount: number,
  totalLabel: string | null | undefined,
): string {
  if (rowCount === 0) return "No results";
  const firstRow = (page - 1) * pageSize + 1;
  const lastRow = (page - 1) * pageSize + rowCount;
  const range = `${firstRow.toLocaleString()}–${lastRow.toLocaleString()}`;
  return totalLabel ? `${range} of ${totalLabel}` : range;
}

function PageNumbers({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number | undefined;
  onPageChange: (page: number) => void;
}) {
  if (!totalPages) {
    return (
      <span className="px-2 text-xs text-muted-foreground tabular-nums">
        Page {page}
      </span>
    );
  }
  return buildPageList(page, totalPages).map((entry, index) =>
    entry === "ellipsis" ? (
      <span
        key={`ellipsis-${index}`}
        className="px-1 text-xs text-muted-foreground"
      >
        …
      </span>
    ) : (
      <Button
        key={entry}
        variant="ghost" aria-pressed={entry === page}
        size="compact"
        className={cn(
          "min-w-7 px-2 text-xs tabular-nums pointer-coarse:min-w-11",
          entry === page && "font-semibold",
        )}
        onClick={() => onPageChange(entry)}
        aria-current={entry === page ? "page" : undefined}
      >
        {entry}
      </Button>
    ),
  );
}

function PageStepButton({
  label,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string;
  icon: LucideIcon;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="compact"
      className="w-7 p-0"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

function PageSizePicker({
  pageSize,
  onPageSizeChange,
}: {
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}) {
  const pageSizes = Array.from(new Set([pageSize, ...PAGE_SIZES])).sort(
    (left, right) => left - right,
  );
  return (
    <div className="w-32">
      <InlineCombobox
        ariaLabel="Rows per page"
        value={String(pageSize)}
        options={pageSizes.map((size) => ({
          value: String(size),
          label: `${size} per page`,
          keywords: [String(size)],
        }))}
        onSelect={(next) => next && onPageSizeChange(Number(next))}
        placeholder="Per page"
        className="text-xs" size="compact"
      />
    </div>
  );
}

export function RecordPagination({
  page,
  pageSize,
  total,
  rowCount,
  canLoadMore,
  onPageChange,
  onPageSizeChange,
}: RecordPaginationProps) {
  const totalPages =
    total.kind === "exact"
      ? Math.max(1, Math.ceil(total.value / pageSize))
      : undefined;
  const hasNext = totalPages
    ? page < totalPages
    : (canLoadMore ?? rowCount === pageSize);

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2">
      <p className="text-xs text-muted-foreground tabular-nums">
        {describeRowRange(page, pageSize, rowCount, formatTotal(total))}
      </p>

      <div className="flex items-center gap-1">
        <PageStepButton
          label="Previous page"
          icon={ChevronLeft}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        />

        <PageNumbers
          page={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />

        <PageStepButton
          label="Next page"
          icon={ChevronRight}
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
        />
      </div>

      <PageSizePicker pageSize={pageSize} onPageSizeChange={onPageSizeChange} />
    </div>
  );
}
